import React, { useState } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FcGoogle } from 'react-icons/fc';
import { useAuth } from '../context/AuthContext';
import { getFirebaseErrorKey } from '../utils/firebaseErrors';

const Login = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const returnTo = searchParams.get('returnTo') || '/';
  const { t } = useTranslation();
  const { login, loginWithGoogle } = useAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (!email || !password) {
      setError(t('auth.fillAllFields'));
      setLoading(false);
      return;
    }

    try {
      await login(email, password);
      navigate(returnTo);
    } catch (err) {
      setError(t(getFirebaseErrorKey(err)));
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setError('');
    setLoading(true);

    try {
      await loginWithGoogle();
      navigate(returnTo);
    } catch (err) {
      console.error('Google login error:', err.code, err.message);
      setError(t(getFirebaseErrorKey(err)));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-card">
        <h2>{t('auth.login')}</h2>
        <p className="auth-subtitle">{t('hero.subtitle')}</p>

        {returnTo !== '/' && (
          <div style={{
            background: 'rgba(108, 92, 231, 0.08)',
            border: '1px solid rgba(108, 92, 231, 0.2)',
            borderRadius: 'var(--radius-sm)',
            padding: '0.75rem 1rem',
            marginBottom: '1rem',
            fontSize: '0.9rem',
            color: 'var(--primary)',
            textAlign: 'center',
          }}>
            🔄 Após o login, você será redirecionado de volta para continuar sua compra
          </div>
        )}

        {error && <div className="error-message">{error}</div>}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="email">{t('auth.email')}</label>
            <input
              id="email"
              type="email"
              className="form-control"
              placeholder="voce@exemplo.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">{t('auth.password')}</label>
            <input
              id="password"
              type="password"
              className="form-control"
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={loading}
            />
          </div>

          <div style={{ textAlign: 'right', marginBottom: '1.5rem' }}>
            <Link to="/forgot-password" style={{ color: 'var(--primary)', fontSize: '0.9rem' }}>
              {t('auth.forgotPassword')}
            </Link>
          </div>

          <button
            type="submit"
            className="btn btn-primary"
            style={{ width: '100%' }}
            disabled={loading}
          >
            {loading ? t('common.loading') : t('auth.login')}
          </button>
        </form>

        <div className="divider">{t('common.or')}</div>

        <button
          type="button"
          className="btn btn-google"
          style={{ width: '100%' }}
          onClick={handleGoogleLogin}
          disabled={loading}
        >
          <FcGoogle style={{ fontSize: '1.2rem' }} />
          {t('auth.googleLogin')}
        </button>

        <div className="auth-footer">
          {t('auth.noAccount')}{' '}
          <Link to={returnTo !== '/' ? `/register?returnTo=${encodeURIComponent(returnTo)}` : '/register'}>
            {t('nav.register')}
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Login;
